import React from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import { BoxProps } from '@material-ui/core';
import Link from 'next/link';
import styled from 'styled-components';

import { useBreakpointUp } from 'qualification/hooks/useBreakpoints';
import ImageWithOverlayAndChatBubble from 'shared/components/ImageWithOverlayAndChatBubble';
import { createSizes } from 'shared/helpers/createSizes';

interface Props extends BoxProps {
  imageSrc: string;
  name: string;
  description: string;
  title: string;
  ctaLink: string;
  ctaLabel: string;
  backgroundColor?: string;
}

export default function ExpertSection({
  imageSrc,
  title,
  name,
  description,
  ctaLink,
  ctaLabel,
  ...boxProps
}: Props) {
  const smUp = useBreakpointUp('sm');

  return (
    <Box pt={{ xs: 2, sm: 0 }} {...boxProps}>
      <WrapperBox
        bgcolor="#F8F8F8"
        px={2}
        maxWidth={{ xs: 'clamp(300px, 70vw, 90vw)', sm: 'none' }}
        mx="auto"
      >
        <ImageWrapperBox
          position="relative"
          mb={3}
          ml={{ xs: 0, md: -7 }}
          mt={{ xs: -3, md: 1 }}
        >
          <ImageWithOverlayAndChatBubble
            src={imageSrc}
            width={smUp ? 180 : 105}
            height={smUp ? 180 : 105}
            objectFit="cover"
            sizes={createSizes({ xs: '120px', sm: '200px' })}
            alt=""
          />
        </ImageWrapperBox>
        <TitleWrapperBox mb={{ xs: 2.2, sm: 0 }} maxWidth={400}>
          <Title variant="h5">{title}</Title>
        </TitleWrapperBox>

        <DescriptionWrapperBox mb={{ xs: 2.2, sm: 1 }}>
          <Text>{name}</Text>

          <Text>{description}</Text>
        </DescriptionWrapperBox>

        <CTAWrapperBox>
          <Box
            display="flex"
            justifyContent={{ xs: 'center', sm: 'flex-start' }}
          >
            <Link href={ctaLink} passHref>
              <CTAButton
                variant="secondary"
                py={0}
                component="a"
                rounded
                target="_blank"
              >
                <Typography
                  fontSize={14}
                  fontStyle="normal"
                  fontWeight={600}
                  lineHeight="172.5%"
                >
                  {ctaLabel}
                </Typography>
              </CTAButton>
            </Link>
          </Box>
        </CTAWrapperBox>
      </WrapperBox>
    </Box>
  );
}

const CTAButton = styled(Button)`
  padding: 6px 15px;
`;

const ImageWrapperBox = styled(Box)`
  grid-area: image;
  width: fit-content;
  justify-self: center;
`;

const DescriptionWrapperBox = styled(Box)`
  grid-area: description;
`;

const TitleWrapperBox = styled(Box)`
  grid-area: title;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-top: ${({ theme }) => theme.spacing(2)}px;
  }
`;

const CTAWrapperBox = styled(Box)`
  grid-area: cta;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-bottom: ${({ theme }) => theme.spacing(2)}px;
  }
`;

const WrapperBox = styled(Box)`
  display: grid;
  grid-template-areas:
    'image'
    'description'
    'title'
    'cta';
  grid-template-columns: 1fr;

  align-items: center;
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing(1, 3, 4, 3)};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding: ${({ theme }) => theme.spacing(1, 5, 1, 6)};

    column-gap: ${({ theme }) => theme.spacing(6)}px;
    row-gap: ${({ theme }) => theme.spacing(2)}px;
    grid-template-areas:
      'image title'
      'image description'
      'image cta';
    grid-template-columns: auto 1fr;
  }
`;

const Title = styled(Typography)`
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  color: ${({ theme }) => theme.palette.secondary['400']};
  text-align: center;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    font-size: 20px;
    text-align: start;
  }
`;

const Text = styled(Typography)`
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    font-size: 16px;
    line-height: 24px;
    text-align: start;
  }
`;
